import { useEffect, useState } from 'react';
import { climbsService } from '../../../services/climbs';
import { climbScansService } from '../../../services/climb-scans';
import type { Climb } from '../../../types/climb';
import type { DetectedHold } from '../../../types/climb-scan';
import type { RouteSetter } from '../../../types/route-setter';
import type { ClimbEditorRoute } from './useClimbEditorRoute';
import { pickBalancedRouteSetter } from './wall-scan';

/** What a scan sent along with the climb it made a draft of. */
export interface ScanSource {
  /** The scan itself, to go back to when this colour is done with. */
  scanId: string;
  /** The photo the analysis ran on: what the shapes below are drawn against. */
  photoUrl: string | null;
  /** One shape per hold the analysis found in this colour, on its 0-1000 grid. */
  detections: DetectedHold[];
  /** Whoever the scan says should get this one, when the draft names nobody. */
  routeSetter: RouteSetter | null;
}

export interface ClimbEditorSource {
  loading: boolean;
  /** The climb this page opened on, when it opened on one. */
  climb: Climb | null;
  scan: ScanSource | null;
}

/**
 * What the editor opens on when it was not opened empty.
 *
 * Two things arrive together and are read as one: the **climb** being worked on
 * — an existing one being corrected, or the draft a scan's "save & insert" just
 * made — and, when a scan sent us here (`?climb_scan_color_id=`), what that scan
 * knows that the climb does not yet: the photo it was taken from, the shapes its
 * analysis found round the holds of this colour, and which setter is next in
 * line.
 *
 * The detections are not turned into marks here. They are on a 0-1000 grid and
 * a mark is a fraction of the photo's own size, so nothing can be placed until
 * the picture has been decoded — see `holdsFromDetections` and the page.
 */
export function useClimbEditorSource(route: ClimbEditorRoute): ClimbEditorSource {
  const { climbSlug, scanColorId, locationSlug, editing } = route;
  const [source, setSource] = useState<ClimbEditorSource>({
    loading: !!climbSlug,
    climb: null,
    scan: null,
  });

  useEffect(() => {
    if (!editing || !climbSlug) return;
    let cancelled = false;
    setSource((current) => ({ ...current, loading: true }));

    const climb = climbsService.getClimbBySlug(climbSlug);
    // Only a gym scans its walls, and the colour is addressed under that gym.
    const scan =
      scanColorId && locationSlug ? loadScan(locationSlug, scanColorId) : Promise.resolve(null);

    Promise.all([climb, scan])
      .then(([loadedClimb, loadedScan]) => {
        if (cancelled) return;
        setSource({ loading: false, climb: loadedClimb, scan: loadedScan });
      })
      .catch(() => {
        // Nothing to open on is not nothing to do: the page falls back to the
        // empty editor rather than to an error, and whatever was already typed
        // stays typed.
        if (!cancelled) setSource({ loading: false, climb: null, scan: null });
      });

    return () => {
      cancelled = true;
    };
  }, [editing, climbSlug, scanColorId, locationSlug]);

  return source;
}

async function loadScan(gymSlug: string, colorId: string): Promise<ScanSource | null> {
  const color = await climbScansService.getColor(gymSlug, colorId);
  const scan = await climbScansService.getScan(gymSlug, color.climb_scan_id);

  return {
    scanId: color.climb_scan_id,
    photoUrl: scan.image_url,
    detections: color.detected_holds ?? [],
    // The draft's own setter wins; this only fills a blank.
    routeSetter: color.climb?.route_setter ?? pickBalancedRouteSetter(scan),
  };
}
